(function () {
  if (window.SummitCartPriceRefresh) return;

  const MIN_REFRESH_INTERVAL = 15000;
  let lastRefreshAt = 0;
  let lastFingerprint = '';
  let inFlightRequest = null;
  let activeAbortController = null;
  let listenersInitialized = false;
  let pendingQuantitySync = null;

  const TIER_KEYS = ['_tier_1_price', '_tier_2_price', '_tier_3_price', '_tier_4_price'];

  function getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const element = selector ? doc.querySelector(selector) : doc.body;
    return element ? element.innerHTML : html;
  }

  function normalizeTierValue(value) {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  function getItemProperties(item) {
    if (!item || !item.properties || typeof item.properties !== 'object') return {};
    return { ...item.properties };
  }

  function getProductPageUrl(item) {
    if (item && item.url) {
      try {
        return new URL(item.url, window.location.origin).toString();
      } catch (_error) {
        return `${window.location.origin}${item.url}`;
      }
    }

    if (item && item.handle) {
      return `${window.location.origin}/products/${item.handle}`;
    }

    return null;
  }

  function parseTierValuesFromHtml(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const tiers = {};

    TIER_KEYS.forEach((key) => {
      const input = doc.querySelector(`input[name="properties[${key}]"]`);
      if (input) {
        tiers[key] = normalizeTierValue(input.value);
      }
    });

    return tiers;
  }

  async function fetchLatestTierValues(item, signal) {
    const productUrl = getProductPageUrl(item);
    if (!productUrl) return null;

    const requestUrl = new URL(productUrl);
    if (item && item.variant_id) {
      requestUrl.searchParams.set('variant', String(item.variant_id));
    }
    requestUrl.searchParams.set('_', String(Date.now()));

    const response = await fetch(requestUrl.toString(), {
      credentials: 'same-origin',
      cache: 'no-store',
      signal,
    });

    if (!response.ok) {
      throw new Error(`Unable to fetch product tiers: ${response.status}`);
    }

    const html = await response.text();
    return parseTierValuesFromHtml(html);
  }

  function mergeUpdatedTierProperties(item, latestTierValues) {
    const currentProps = getItemProperties(item);
    const mergedProps = { ...currentProps };
    let changed = false;

    TIER_KEYS.forEach((key) => {
      const latestValue = normalizeTierValue(latestTierValues?.[key]);
      const currentValue = normalizeTierValue(currentProps[key]);

      if (!latestValue) return;
      if (latestValue !== currentValue) {
        mergedProps[key] = latestValue;
        changed = true;
      }
    });

    return {
      changed,
      properties: mergedProps,
    };
  }

  async function updateCartLineProperties(id, quantity, properties, signal) {
    const cartChangeUrl = routes.cart_change_url.endsWith('.js')
      ? routes.cart_change_url
      : `${routes.cart_change_url}.js`;

    const response = await fetch(cartChangeUrl, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id,
        quantity,
        properties,
      }),
      signal,
    });

    if (!response.ok) {
      throw new Error(`Unable to update cart line tiers: ${response.status}`);
    }
  }

  async function syncCartTierProperties(cartState, signal) {
    if (!cartState || !Array.isArray(cartState.items) || !cartState.items.length) {
      return { updated: false };
    }

    const tierFetches = cartState.items.map((item) =>
      fetchLatestTierValues(item, signal).catch(() => null)
    );
    const allTierValues = await Promise.all(tierFetches);

    const pendingUpdates = [];
    cartState.items.forEach((item, index) => {
      const latestTierValues = allTierValues[index];
      if (!latestTierValues || !Object.keys(latestTierValues).length) return;

      const merged = mergeUpdatedTierProperties(item, latestTierValues);
      if (!merged.changed) return;

      pendingUpdates.push({ id: item.key, quantity: item.quantity, properties: merged.properties });
    });

    if (!pendingUpdates.length) return { updated: false };

    for (const update of pendingUpdates) {
      await updateCartLineProperties(update.id, update.quantity, update.properties, signal);
    }

    return { updated: true };
  }

  function buildFingerprint(cartState) {
    if (!cartState || !Array.isArray(cartState.items)) return '';
    const lineFingerprint = cartState.items
      .map((item) => {
        const tierValues = TIER_KEYS.map((key) => normalizeTierValue(item.properties?.[key]));
        return `${item.key}:${item.quantity}:${item.final_price}:${item.original_price}:${tierValues.join(':')}`;
      })
      .join('|');

    return `${cartState.item_count}:${cartState.total_price}:${lineFingerprint}`;
  }

  function isCartVisible() {
    if (document.querySelector('cart-items')) return true;
    const cartDrawer = document.querySelector('cart-drawer');
    return !!(cartDrawer && cartDrawer.classList.contains('active'));
  }

  function getRenderableTargets() {
    const targets = [];
    const cartItems = document.querySelector('cart-items');
    const cartDrawer = document.querySelector('cart-drawer');
    const cartDrawerItems = document.querySelector('cart-drawer-items');

    if (cartItems) {
      targets.push(cartItems);
    }

    if (cartDrawerItems && cartDrawer && cartDrawer.classList.contains('active')) {
      targets.push(cartDrawerItems);
    }

    return targets;
  }

  function getSectionDefinitions(targets) {
    return targets
      .filter((target) => target && typeof target.getSectionsToRender === 'function')
      .flatMap((target) => target.getSectionsToRender())
      .filter((section) => section && section.id && section.section);
  }

  function uniqueSectionIds(sectionDefinitions) {
    return [...new Set(sectionDefinitions.map((section) => section.id))];
  }

  function setRefreshingUiState(isRefreshing) {
    document.documentElement.classList.toggle('cart-price-refreshing', Boolean(isRefreshing));
  }

  function updateEmptyStates(itemCount) {
    const cartPage = document.getElementById('main-cart');
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartPage) {
      cartPage.classList.toggle('is-empty', itemCount === 0);
    }

    if (cartDrawer) {
      cartDrawer.classList.toggle('is-empty', itemCount === 0);
      const drawerInner = cartDrawer.querySelector('.drawer__inner');
      if (drawerInner) {
        drawerInner.classList.toggle('is-empty', itemCount === 0);
      }
    }
  }

  function applySectionUpdates(sectionDefinitions, sectionsMap) {
    sectionDefinitions.forEach((section) => {
      const target =
        document.getElementById(section.section)?.querySelector(section.selector) ||
        document.getElementById(section.section);

      if (!target || !sectionsMap[section.id]) return;

      target.innerHTML = getSectionInnerHTML(sectionsMap[section.id], section.selector);
    });
  }

  function updateProductRecommendations(targets) {
    targets.forEach((target) => {
      if (typeof target.updateProductRecommendation === 'function') {
        target.updateProductRecommendation();
      }
    });
  }

  async function fetchCartState(signal) {
    const response = await fetch(`${routes.cart_url}.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
      signal,
    });

    if (!response.ok) {
      throw new Error(`Unable to fetch cart state: ${response.status}`);
    }

    return response.json();
  }

  async function fetchSectionMarkup(sectionIds, signal) {
    if (!sectionIds.length) return {};

    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('sections', sectionIds.join(','));
    url.searchParams.set('_', Date.now().toString());

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      cache: 'no-store',
      signal,
    });

    if (!response.ok) {
      throw new Error(`Unable to fetch cart sections: ${response.status}`);
    }

    return response.json();
  }

  async function refreshCartPrices(options = {}) {
    const { reason = 'manual', force = false, skipSectionRender = false } = options;

    if (inFlightRequest && !skipSectionRender) {
      return inFlightRequest;
    }

    if (inFlightRequest && skipSectionRender) {
      return inFlightRequest.then(() => refreshCartPrices(options));
    }

    const now = Date.now();
    if (!force && now - lastRefreshAt < MIN_REFRESH_INTERVAL) {
      return { refreshed: false, reason: 'throttled' };
    }

    activeAbortController = new AbortController();
    setRefreshingUiState(true);

    inFlightRequest = (async () => {
      const targets = getRenderableTargets();
      if (!targets.length) {
        return { refreshed: false, reason: 'no-targets' };
      }

      let cartState = await fetchCartState(activeAbortController.signal);
      const tierSyncResult = await syncCartTierProperties(cartState, activeAbortController.signal);
      if (tierSyncResult.updated) {
        cartState = await fetchCartState(activeAbortController.signal);
      }

      const currentFingerprint = buildFingerprint(cartState);

      if (!skipSectionRender) {
        if (!force && currentFingerprint === lastFingerprint) {
          lastRefreshAt = Date.now();
          return { refreshed: false, reason: 'unchanged' };
        }

        const sectionDefinitions = getSectionDefinitions(targets);
        const sectionIds = uniqueSectionIds(sectionDefinitions);
        const sectionsMap = await fetchSectionMarkup(sectionIds, activeAbortController.signal);

        applySectionUpdates(sectionDefinitions, sectionsMap);
        updateEmptyStates(cartState.item_count || 0);
        updateProductRecommendations(targets);
      }

      lastFingerprint = currentFingerprint;
      lastRefreshAt = Date.now();

      document.dispatchEvent(
        new CustomEvent('afterCartPricesRefreshed', {
          detail: {
            source: 'cart-price-refresh',
            reason,
            itemCount: cartState.item_count || 0,
          },
        })
      );

      return { refreshed: true, reason };
    })()
      .catch((error) => {
        if (error && error.name === 'AbortError') {
          return { refreshed: false, reason: 'aborted' };
        }

        console.error('[cart-price-refresh] refresh failed', error);
        return { refreshed: false, reason: 'error' };
      })
      .finally(() => {
        inFlightRequest = null;
        setRefreshingUiState(false);
      });

    return inFlightRequest;
  }

  function setupEventListeners() {
    if (listenersInitialized) return;
    listenersInitialized = true;

    document.addEventListener('afterCartChanged', () => {
      refreshCartPrices({ reason: 'after-add-to-cart', force: true });
    });

    document.addEventListener('afterUpdateQuantity', () => {
      if (pendingQuantitySync) {
        clearTimeout(pendingQuantitySync);
      }
      pendingQuantitySync = setTimeout(() => {
        pendingQuantitySync = null;
        refreshCartPrices({ reason: 'after-quantity-change', force: true, skipSectionRender: true });
      }, 80);
    });

    document.addEventListener('cartDrawerOpened', () => {
      refreshCartPrices({ reason: 'cart-drawer-open', force: true });
    });

    window.addEventListener('focus', () => {
      if (!isCartVisible()) return;
      refreshCartPrices({ reason: 'window-focus' });
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && isCartVisible()) {
        refreshCartPrices({ reason: 'tab-visible' });
      }
    });

    window.addEventListener('pageshow', () => {
      if (!isCartVisible()) return;
      refreshCartPrices({ reason: 'page-show', force: true });
    });

    document.addEventListener('DOMContentLoaded', () => {
      if (document.querySelector('cart-items')) {
        refreshCartPrices({ reason: 'cart-page-load', force: true });
      }
    });
  }

  window.SummitCartPriceRefresh = {
    refreshCartPrices,
    setupEventListeners,
  };

  setupEventListeners();
})();
